import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftIcon, ChevronRightIcon, ExclamationCircleIcon, EnvelopeIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { BramkaTresci } from '../components/auth/BramkaLogowania'
import * as api from '../api/rynek'
import { SPRZET_ZLECENIA, STATUS_DLA_KOWALSKIEGO, ZAKRESY_FIRMY } from '../config/rynek'
import { kodOgloszenia, wierszeOpisu, zl } from '../lib/rynek'
import { kartaZUczestnika, wykonawcow } from '../lib/wykonawcy'
import { zdarzenie } from '../lib/pomiar'
import ListaWykonawcow from '../components/rynek/ListaWykonawcow'
import RealizacjaZlecenia from '../components/rynek/RealizacjaZlecenia'

/**
 * „Moje ogłoszenia” na Rynku (`/rynek/moje`, `/rynek/moje/:id`, gdzie `:id` to kod ogłoszenia): ogłoszenia Kowalskiego
 * i odpowiedzi firm. W trybie dopasowania (2026-10-22) sekcja „Firmy z Twoim kontaktem”: nazwy firm, które dostały
 * kontakt, i „Nie kontaktujcie się już ze mną” przy każdej (`POST /ogloszenia/{kod}/firmy/{id}/koniec-kontaktu`).
 * Przy odpowiedzi: „Wybieram tę firmę” (firma dostaje kontakt i znak, że wygrała), „Udostępnij kontakt” (bez wyboru)
 * i „Odrzuć”. Kontakt do firmy (e-mail osoby, która odpowiedziała) widać dopiero po udostępnieniu kontaktu.
 *
 * Mini przetarg (2026-10-24, `ogl.przetarg`): od makiety „VoltValue dla domu” 12 lista wykonawców w kartach
 * (`components/rynek/ListaWykonawcow`: logo, zdjęcia, ocena Google, specjalizacje, certyfikat, oferta, „w budżecie”),
 * w kolejności losowej stałej dla zlecenia (to nie ranking). Klient zaznacza wykonawców i klika „Przekaż kontakt
 * wybranym” (`POST /ogloszenia/{kod}/wybierz-firmy`); pozostali dostają informację, że nie przeszli dalej. Przy
 * wykonawcy z kontaktem „Nie kontaktujcie się już ze mną” (`koniec-kontaktu` z id udziału).
 *
 * Wariant D (pakiet P0): liczniki `oferty` i `dla_ciebie` oraz zakres `zakres_od`/`zakres_do`; dopóki backend ich nie
 * zwraca, lista mówi o wykonawcach, którzy potwierdzili udział. W tekstach dla klienta „wykonawca” (D17).
 */

/** Status do pokazania: zlecenie gościa czekające na potwierdzenie e-maila może przyjść jako `stan` albo `status`. */
const statusZlecenia = (o) => (o.stan === 'czeka_na_email' ? 'czeka_na_email' : o.status)

const Status = ({ status }) => {
  const s = STATUS_DLA_KOWALSKIEGO[status] || { nazwa: status, kolor: 'bg-gray-400' }
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
      <span className={`h-2 w-2 rounded-full ${s.kolor}`} aria-hidden="true" />{s.nazwa}
    </span>
  )
}

/** Kto zajmie się sprzętem słowami z makiety (pola `kto_dobiera`, `kto_kupuje` z pakietu P0) albo dawna nazwa. */
const sprzetZlecenia = (ogl) => SPRZET_ZLECENIA.find((o) => o.kto_dobiera === ogl.kto_dobiera && o.kto_kupuje === ogl.kto_kupuje)?.nazwa
  || ogl.sprzet_nazwa || null

const data = (iso) => new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })

/** Co robi firma (ankieta firmy): „Instalacja ze sprzętem firmy”, „Montaż Twojego sprzętu”, „Serwis” (kolejność jak w ankiecie). */
const ZakresFirmy = ({ zakres }) => {
  const z = ZAKRESY_FIRMY.filter((x) => zakres?.includes(x.klucz))
  if (!z.length) return null
  return (
    <span className="flex flex-wrap gap-1.5">
      {z.map((x) => <span key={x.klucz} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11.5px] font-semibold text-gray-700">{x.znacznik}</span>)}
    </span>
  )
}
const odmianaOdpowiedzi = (n) => (n === 1 ? 'odpowiedź' : 'odpowiedzi')

const terminTekst = (iso) => new Date(iso).toLocaleString('pl-PL', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })

/**
 * Dobór wykonawców u klienta (wariant D, pakiet P0): w trakcie 48 h same liczby (zaproszeni, oferty, odmowy) i termin,
 * potem wykonawcy dobrani do zlecenia (`przetarg.finalisci`) w kartach z ofertą i wybór, kto dostanie kontakt.
 * Starsze zlecenia (wariant A) mają `uczestnicy` zamiast `finalisci`. Bez słów „przetarg”, „konkurs”, „finaliści”.
 */
const Przetarg = ({ ogl, busy, onWybierz, onKoniec }) => {
  const p = ogl.przetarg
  const lista = Array.isArray(p.finalisci) ? p.finalisci : (p.uczestnicy || [])
  const karty = lista.map(kartaZUczestnika)
  const wariantD = Array.isArray(p.finalisci) || p.oferty != null
  const zlozyli = p.oferty ?? p.potwierdzone
  const zakres = p.zakres?.od && p.zakres?.do ? p.zakres : (ogl.zakres_od && ogl.zakres_do ? { od: ogl.zakres_od, do: ogl.zakres_do } : null)
  const doWyboru = wariantD ? 'finalista' : 'potwierdzona'
  const wybor = ogl.otwarte && !p.rozstrzygniety && lista.some((u) => u.stan === doWyboru)

  let info
  if (statusZlecenia(ogl) === 'czeka_na_email') {
    info = 'Czeka na potwierdzenie e-maila. Kliknij link z wiadomości, którą wysłaliśmy przy zakładaniu konta, a zlecenie trafi do wykonawców.'
  } else if (ogl.status === 'nowe') {
    info = 'Sprawdzamy zlecenie. Potem zaprosimy pasujących wykonawców z Twojej okolicy, a oni w 48 godzin złożą ofertę albo odmówią.'
  } else if (p.brak_firm) {
    info = 'Minął termin i żaden wykonawca nie złożył oferty. To zlecenie nie przyjmuje już ofert. Jeśli nadal szukasz wykonawcy, możesz wystawić nowe zlecenie.'
  } else if (p.rozstrzygniety) {
    info = 'Wybrani wykonawcy mają Twój kontakt. Pozostali dostali informację, że nie przeszli dalej. Kolejność na liście jest losowa.'
  } else if (karty.length && wariantD) {
    info = `Ofertę do Twojego zlecenia złożyło ${wykonawcow(zlozyli)}. Oto ${karty.length} najlepiej dopasowanych do okolicy, zakresu prac i sprzętu. `
      + (zakres
        ? `Każdy przyjął warunki zlecenia i wycenił je w zakresie ${Math.round(zakres.od).toLocaleString('pl-PL')} do ${zl(zakres.do)} albo zaznaczył, o ile go przekracza. `
        : 'Każdy przyjął warunki zlecenia. ')
      + 'Kolejność na liście jest losowa.'
  } else if (karty.length) {
    info = 'Wykonawcy, którzy potwierdzili udział i przygotowali ofertę. Kolejność losowa, to nie ranking. Odmowy pokazujemy tylko jako liczbę.'
  } else {
    info = `Czekamy na oferty wykonawców${p.czekamy_do ? `, najpóźniej do ${terminTekst(p.czekamy_do)}` : ''}. `
      + `Potem pokażemy Ci ${wykonawcow(p.liczba_firm || 3)} najlepiej dopasowanych i wyślemy Ci e-mail.`
  }

  return (
    <section aria-labelledby="przetarg" className="karta karta-lista">
      <div className="karta-glowa"><h2 id="przetarg" className="karta-nazwa">Wykonawcy dobrani do Twojego zlecenia</h2></div>
      {ogl.status !== 'nowe' && statusZlecenia(ogl) !== 'czeka_na_email' && !p.brak_firm && (
        <div className="stan-zlecenia" aria-label="Stan zaproszeń">
          <span className="licz"><i style={{ background: 'var(--line-2)' }} />Zaproszeni: {p.zaproszone}</span>
          <span className="licz"><i style={{ background: 'var(--blue)' }} />{wariantD ? 'Złożyli ofertę' : 'Potwierdzili udział'}: {zlozyli}</span>
          {wariantD && !karty.length && p.czekajace > 0 && <span className="licz"><i style={{ background: 'var(--amber)' }} />Czekamy na: {p.czekajace}</span>}
          {karty.length > 0 && <span className="licz"><i style={{ background: '#10B981' }} />Dla Ciebie: {karty.length}</span>}
        </div>
      )}
      <p className="kolejnosc-info" style={{ paddingBottom: karty.length ? 0 : 16 }}>{info}</p>
      {karty.length > 0 && (
        <ListaWykonawcow karty={karty} zakresDo={zakres?.do ?? null} wybor={wybor} poPrzekazaniu={Boolean(p.rozstrzygniety)}
                         busy={busy} onPrzekaz={onWybierz} onKoniec={onKoniec} />
      )}
    </section>
  )
}

const Ramka = ({ children }) => (
  <div className="vv-dom bg-gray-50 text-gray-900">
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:px-6 sm:pt-12">{children}</div>
  </div>
)

const Lista = () => {
  const [stan, setStan] = useState({ ladowanie: true, blad: '', lista: [] })
  useEffect(() => {
    api.mojeOgloszenia().then((r) => setStan({ ladowanie: false, blad: r.ok ? '' : r.error, lista: r.ok ? r.data : [] }))
  }, [])
  if (stan.ladowanie) return <p className="text-sm text-gray-500">Wczytuję…</p>
  if (stan.blad) return <p role="alert" className="text-sm text-red-700">{stan.blad}</p>
  if (!stan.lista.length) {
    return (
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 text-center">
        <p className="m-0 text-[15px] text-gray-700">Nie masz jeszcze zleceń.</p>
        <Link to="/rynek" className="mt-4 inline-flex h-[46px] items-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white no-underline hover:bg-blue-700">
          Wystaw zlecenie
        </Link>
      </div>
    )
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {stan.lista.map((o) => (
        <li key={o.id}>
          <Link to={`/rynek/moje/${kodOgloszenia(o)}`}
                className="group flex items-center gap-4 rounded-[18px] border border-gray-200 bg-white p-4 no-underline transition hover:border-blue-400 hover:shadow-sm">
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[15px] font-semibold text-gray-900">{o.typ_nazwa}, {o.miejscowosc}</p>
              <p className="m-0 mt-0.5 text-[13px] text-gray-500">Wystawione {data(o.created_at)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <Status status={statusZlecenia(o)} />
                <span className="text-[13px] text-gray-600">
                  {o.przetarg && `Oferty: ${o.przetarg.oferty ?? o.przetarg.potwierdzone ?? 0} · `}
                  {o.liczba_odpowiedzi} {odmianaOdpowiedzi(o.liczba_odpowiedzi)}
                  {o.nowe_odpowiedzi > 0 && <span className="ml-1.5 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">{o.nowe_odpowiedzi} nowe</span>}
                </span>
              </div>
            </div>
            <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-300 group-hover:text-blue-600" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

const Odpowiedz = ({ o, otwarte, busy, onAkcja }) => {
  const wybrana = o.status === 'wybrana'
  return (
    <li className={`rounded-[18px] border bg-white p-4 sm:p-5 ${wybrana ? 'border-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="m-0 flex items-center gap-2 text-[15px] font-semibold text-gray-900">
            {o.firma.nazwa}
            {o.firma.zweryfikowana && <ShieldCheckIcon className="h-4 w-4 text-emerald-600" aria-label="Dane firmy z Białej listy VAT" />}
            {o.nowa && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">nowa</span>}
          </p>
          {o.firma.miasto && <p className="m-0 text-[13px] text-gray-500">{o.firma.miasto}</p>}
          <div className="mt-1.5"><ZakresFirmy zakres={o.firma.zakres} /></div>
        </div>
        <div className="text-right">
          <p className="m-0 text-xl font-bold text-gray-900">{o.cena_brutto != null ? zl(o.cena_brutto) : 'cena do ustalenia'}</p>
          {o.cena_brutto != null && <p className="m-0 text-[12px] text-gray-500">brutto, orientacyjnie</p>}
        </div>
      </div>
      {o.termin_montazu && <p className="m-0 mt-3 text-sm text-gray-700"><span className="text-gray-500">Termin montażu:</span> {o.termin_montazu}</p>}
      <p className="m-0 mt-2 whitespace-pre-line text-sm leading-6 text-gray-800">{o.wiadomosc}</p>

      {wybrana && <p className="m-0 mt-3 text-sm font-semibold text-emerald-700">Ten wykonawca jest wybrany i ma już Twój kontakt.</p>}
      {o.kontakt_udostepniony && o.email_firmy && (
        <a href={`mailto:${o.email_firmy}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 no-underline hover:underline">
          <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />{o.email_firmy}
        </a>
      )}
      {o.kontakt_udostepniony && !wybrana && <p className="m-0 mt-2 text-[13px] text-gray-500">Wykonawca ma Twój kontakt.</p>}
      {o.status === 'odrzucona' && <p className="m-0 mt-3 text-[13px] text-gray-500">Odrzucona.</p>}

      {otwarte && o.status === 'wyslana' && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy} onClick={() => onAkcja('wybierz', o.id)}
                  className="h-[42px] rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            Wybieram tego wykonawcę
          </button>
          {!o.kontakt_udostepniony && (
            <button type="button" disabled={busy} onClick={() => onAkcja('kontakt', o.id)}
                    className="h-[42px] rounded-xl border-[1.5px] border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 hover:border-gray-400 disabled:opacity-60">
              Udostępnij kontakt
            </button>
          )}
          <button type="button" disabled={busy} onClick={() => onAkcja('odrzuc', o.id)}
                  className="h-[42px] rounded-xl px-3 text-sm font-semibold text-gray-500 hover:text-red-700 disabled:opacity-60">
            Odrzuć
          </button>
        </div>
      )}
    </li>
  )
}

/** Wykonawcy, którzy mają kontakt klienta (od razu w trybie dopasowania, inaczej po wyborze), z prośbą o koniec kontaktu.
 *  W mini przetargu kontakt i prośba są na kartach listy, więc tej sekcji tam nie ma. */
const FirmyZKontaktem = ({ ogl, busy, onKoniec }) => {
  const [pytam, setPytam] = useState(null)
  if (!ogl.firmy_z_kontaktem?.length) {
    return ogl.tryb_kontaktu === 'dopasowanie' && ogl.otwarte ? (
      <p className="m-0 rounded-[18px] border border-dashed border-gray-300 bg-white p-5 text-sm leading-6 text-gray-600">
        Dobieramy wykonawców z Twojej okolicy. Gdy ich pokażemy, napiszemy, którzy to wykonawcy, a oni zadzwonią z ofertą.
      </p>
    ) : null
  }
  return (
    <section aria-labelledby="firmy-z-kontaktem">
      <h2 id="firmy-z-kontaktem" className="m-0 text-lg font-bold">Wykonawcy z Twoim kontaktem</h2>
      <p className="m-0 mt-1 text-sm text-gray-600">Ci wykonawcy mogą do Ciebie zadzwonić albo napisać w sprawie tego zlecenia.</p>
      <ul className="m-0 mt-3 flex list-none flex-col divide-y divide-gray-100 rounded-[18px] border border-gray-200 bg-white p-0">
        {ogl.firmy_z_kontaktem.map((f) => (
          <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <p className="m-0 text-[15px] font-semibold text-gray-900">{f.nazwa}</p>
              <p className="m-0 text-[13px] text-gray-500">{[f.miasto, f.od && `kontakt od ${data(f.od)}`].filter(Boolean).join(' · ')}</p>
              <div className="mt-1"><ZakresFirmy zakres={f.zakres} /></div>
              {f.telefony?.length > 0 && <p className="m-0 mt-1 text-[13px] text-gray-700">Telefon: {f.telefony.join(', ')}</p>}
            </div>
            {f.koniec_kontaktu ? (
              <span className="text-[13px] font-semibold text-gray-500">Prośba o koniec kontaktu wysłana</span>
            ) : pytam === f.id ? (
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] text-gray-700">Wykonawca nie będzie się już kontaktować. Na pewno?</span>
                <button type="button" disabled={busy} onClick={() => { onKoniec(f.id); setPytam(null) }}
                        className="h-[38px] rounded-xl bg-gray-900 px-3.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-60">Tak</button>
                <button type="button" onClick={() => setPytam(null)} className="h-[38px] px-2 text-sm font-semibold text-gray-600">Anuluj</button>
              </span>
            ) : (
              <button type="button" onClick={() => setPytam(f.id)}
                      className="text-sm font-semibold text-gray-500 hover:text-red-700">Nie kontaktujcie się już ze mną</button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

const Szczegoly = ({ id }) => {
  const navigate = useNavigate()
  const [ogl, setOgl] = useState(null)
  const [blad, setBlad] = useState('')
  const [busy, setBusy] = useState(false)
  const [zamykam, setZamykam] = useState(false)

  useEffect(() => {
    api.mojeOgloszenie(id).then((r) => {
      if (!r.ok) { setBlad(r.error); return }
      setOgl(r.data)
      // Stary link z maila z liczbowym id: w pasku adresu zostaje kod.
      if (r.data.kod && r.data.kod !== id) navigate(`/rynek/moje/${r.data.kod}`, { replace: true })
    })
  }, [id, navigate])

  const akcja = useCallback(async (rodzaj, odpowiedzId) => {
    setBusy(true)
    setBlad('')
    const fn = { wybierz: api.wybierzOdpowiedz, kontakt: api.udostepnijKontakt, odrzuc: api.odrzucOdpowiedz }[rodzaj]
    const r = await fn(odpowiedzId)
    setBusy(false)
    if (r.ok) setOgl(r.data)
    else setBlad(r.error)
  }, [])

  const wybierzFirmy = async (udzialy) => {
    setBusy(true)
    setBlad('')
    const r = await api.wybierzFirmy(ogl.kod || id, udzialy)
    setBusy(false)
    if (r.ok) { setOgl(r.data); zdarzenie('rynek_kontakt', String(udzialy.length)) }
    else setBlad(r.error)
  }

  const koniecKontaktu = async (udostepnienieId) => {
    setBusy(true)
    setBlad('')
    const r = await api.koniecKontaktu(ogl.kod || id, udostepnienieId)
    setBusy(false)
    if (r.ok) setOgl(r.data)
    else setBlad(r.error)
  }

  const zamknij = async () => {
    setBusy(true)
    const r = await api.zamknijOgloszenie(id)
    setBusy(false)
    setZamykam(false)
    if (r.ok) setOgl(r.data)
    else setBlad(r.error)
  }

  if (!ogl) return blad ? <p role="alert" className="text-sm text-red-700">{blad}</p> : <p className="text-sm text-gray-500">Wczytuję…</p>
  const opis = wierszeOpisu(ogl.opis)
  const budzet = ogl.budzet_kwota ?? ogl.budzet_brutto

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-[20px] border border-gray-200 bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-bold">{ogl.typ_nazwa}</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">
              {[ogl.miejscowosc, ogl.powiat, ogl.wojewodztwo_nazwa].filter(Boolean).join(', ')} · wystawione {data(ogl.created_at)}
            </p>
          </div>
          <Status status={statusZlecenia(ogl)} />
        </div>
        {opis.length > 0 && (
          <dl className="m-0 mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {opis.map(([e, w]) => (
              <div key={e}><dt className="text-[12.5px] text-gray-500">{e}</dt><dd className="m-0 text-sm font-semibold">{w}</dd></div>
            ))}
          </dl>
        )}
        <p className="m-0 mt-3 text-sm text-gray-600">
          {[ogl.termin_nazwa, sprzetZlecenia(ogl), budzet ? `budżet około ${zl(budzet)}` : null,
            ogl.zakres_od && ogl.zakres_do ? `wykonawcy widzą zakres ${Math.round(ogl.zakres_od).toLocaleString('pl-PL')} do ${zl(ogl.zakres_do)}` : null,
            ogl.pme2 ? 'dotacja PME2' : null].filter(Boolean).join(' · ')}
        </p>
        {(ogl.ulica || ogl.nr_domu) && (
          <p className="m-0 mt-1 text-sm text-gray-600">
            Adres: {[ogl.ulica, ogl.nr_domu].filter(Boolean).join(' ')}. Zobaczą go tylko wykonawcy, których wybierzesz.
          </p>
        )}
        {ogl.komentarz && <p className="m-0 mt-3 whitespace-pre-line rounded-xl bg-gray-50 p-3 text-sm text-gray-700">{ogl.komentarz}</p>}
      </section>

      {blad && (
        <p role="alert" className="m-0 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{blad}
        </p>
      )}

      {ogl.przetarg && <Przetarg ogl={ogl} busy={busy} onWybierz={wybierzFirmy} onKoniec={koniecKontaktu} />}

      {!ogl.przetarg && <FirmyZKontaktem ogl={ogl} busy={busy} onKoniec={koniecKontaktu} />}

      {!ogl.przetarg && (
      <section aria-labelledby="odpowiedzi">
        <h2 id="odpowiedzi" className="m-0 text-lg font-bold">Odpowiedzi wykonawców{ogl.liczba_odpowiedzi ? ` (${ogl.liczba_odpowiedzi})` : ''}</h2>
        {ogl.odpowiedzi.length ? (
          <ul className="m-0 mt-3 flex list-none flex-col gap-3 p-0">
            {ogl.odpowiedzi.map((o) => <Odpowiedz key={o.id} o={o} otwarte={ogl.otwarte} busy={busy} onAkcja={akcja} />)}
          </ul>
        ) : (
          <p className="m-0 mt-2 rounded-[18px] border border-dashed border-gray-300 bg-white p-5 text-sm leading-6 text-gray-600">
            {!ogl.otwarte ? 'Zlecenie jest zamknięte.'
              : ogl.tryb_kontaktu === 'dopasowanie'
                ? 'Wykonawcy, którzy mają Twój kontakt, zadzwonią i dodadzą tu swoje oferty. Dostaniesz o tym e-mail.'
                : 'Pokazujemy zlecenie wykonawcom z Twojej okolicy. Gdy któryś odpowie, dostaniesz e-mail.'}
          </p>
        )}
      </section>
      )}

      {/* Umowa i montaż z wykonawcą (2026-11-03): cicho na dole, przydaje się tygodnie po wyborze. */}
      {(ogl.firmy_z_kontaktem || []).map((f) => (
        <RealizacjaZlecenia key={f.id} stan={f.realizacja} strona="klient" tytul={`Umowa i montaż: ${f.nazwa}`}
                            onZapisz={async (dane) => {
                              const r = await api.realizacjaKlienta(ogl.kod || id, f.id, dane)
                              if (r.ok) setOgl(r.data)
                              return r
                            }} />
      ))}

      {ogl.otwarte && (
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-5">
          {zamykam ? (
            <>
              <span className="text-sm text-gray-700">
                {ogl.przetarg ? 'Zamknąć zlecenie? Wykonawcy, którzy się zgłosili, dostaną informację, a nikt nie dostanie Twojego kontaktu.'
                  : 'Zamknąć zlecenie? Wykonawcy nie będą mogli już odpowiadać.'}
              </span>
              <button type="button" disabled={busy} onClick={zamknij}
                      className="h-[40px] rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white hover:bg-gray-700">Zamknij</button>
              <button type="button" onClick={() => setZamykam(false)} className="h-[40px] px-3 text-sm font-semibold text-gray-600">Anuluj</button>
            </>
          ) : (
            <button type="button" onClick={() => setZamykam(true)} className="text-sm font-semibold text-gray-500 hover:text-gray-800">
              Zamknij zlecenie
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const RynekMoje = () => {
  const { id } = useParams()
  useDocumentTitle(id ? 'Zlecenie na Rynku' : 'Moje zlecenia na Rynku')
  return (
    <Ramka>
      <Link to={id ? '/rynek/moje' : '/rynek'} className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-blue-700">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />{id ? 'Moje zlecenia' : 'Rynek'}
      </Link>
      {!id && <h1 className="m-0 mb-5 text-[28px] font-bold tracking-[-0.01em]">Moje zlecenia</h1>}
      <BramkaTresci bramka="rynek" tryb="zamiast" tytul="Twoje zlecenia po zalogowaniu"
                    opis="Zaloguj się, żeby zobaczyć oferty wykonawców do Twoich zleceń.">
        {id ? <Szczegoly id={id} /> : <Lista />}
      </BramkaTresci>
    </Ramka>
  )
}

export default RynekMoje
